import type { JSX } from 'preact';

interface Row {
  label: string;
  tanren: JSX.Element;
  openai: JSX.Element;
  anthropic: JSX.Element;
  openclaw: JSX.Element;
}

const rows: Row[] = [
  {
    label: 'Token limits',
    tanren: <><span class="check">Unlimited</span> — no caps, no throttle</>,
    openai: <><span class="cross">Capped</span> — usage limits per tier</>,
    anthropic: <><span class="cross">Capped</span> — slow mode at limits</>,
    openclaw: <><span class="partial">Varies</span></>,
  },
  {
    label: 'Pricing model',
    tanren: <><span class="check">Pay per GPU hour</span> — not per token</>,
    openai: <><span class="cross">$20+/mo subscription</span></>,
    anthropic: <><span class="cross">$20+/mo subscription</span></>,
    openclaw: <><span class="check">Free</span></>,
  },
  {
    label: 'Runs on real GPU',
    tanren: <><span class="check">Yes</span> — dedicated GPU session</>,
    openai: <><span class="cross">Shared cloud endpoint</span></>,
    anthropic: <><span class="cross">Shared cloud endpoint</span></>,
    openclaw: <><span class="partial">Varies</span></>,
  },
  {
    label: 'Data privacy',
    tanren: <><span class="check">Full</span> — your own session</>,
    openai: <><span class="cross">Data sent to OpenAI</span></>,
    anthropic: <><span class="cross">Data sent to Anthropic</span></>,
    openclaw: <><span class="check">Local</span></>,
  },
  {
    label: 'Persistent memory',
    tanren: <><span class="check">Yes</span> — vector DB + RAG</>,
    openai: <><span class="partial">Basic</span> — cloud memory</>,
    anthropic: <><span class="cross">Per-project only</span></>,
    openclaw: <><span class="cross">No</span></>,
  },
  {
    label: 'Agentic tool use',
    tanren: <><span class="check">Yes</span> — files, shell, web, git</>,
    openai: <><span class="partial">Limited</span> — sandboxed plugins</>,
    anthropic: <><span class="partial">Limited</span> — via MCP</>,
    openclaw: <><span class="partial">Partial</span></>,
  },
  {
    label: 'Context window',
    tanren: <><span class="check">100K</span></>,
    openai: <><span class="partial">128K</span></>,
    anthropic: <><span class="check">200K</span></>,
    openclaw: <><span class="cross">Varies</span></>,
  },
];

const thBase =
  'bg-[oklch(14%_0.01_250)] px-5 py-4 text-left font-display font-bold text-[0.85rem] tracking-[-0.01em] text-fg border-b border-border';

function Comparison() {
  return (
    <section class="section" id="compare">
      <div class="container">
        <div class="section-header">
          <span class="tag">Compare</span>
          <h2>Platform vs. cloud APIs</h2>
        </div>
        <div class="comparison-wrapper overflow-x-auto">
          <table class="w-full border-separate border-spacing-0 text-[0.9rem] overflow-hidden rounded-radius border border-border">
            <thead>
              <tr>
                <th class={`${thBase} rounded-tl-radius w-[22%]`}></th>
                <th class="bg-[oklch(18%_0.03_25)] text-accent relative px-5 py-4 text-left font-display font-bold text-[0.85rem] tracking-[-0.01em] border-b border-border w-[19.5%] after:content-[''] after:absolute after:top-0 after:left-0 after:right-0 after:h-[2px] after:bg-accent">
                  TanrenAI
                </th>
                <th class={`${thBase} w-[19.5%]`}>ChatGPT / OpenAI</th>
                <th class={`${thBase} w-[19.5%]`}>Claude / Anthropic</th>
                <th class={`${thBase} rounded-tr-radius w-[19.5%]`}>OpenClaw</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row, i) => {
                const isLast = i === rows.length - 1;
                const border = isLast ? '' : 'border-b border-border';
                return (
                  <tr key={i}>
                    <td class={`px-5 py-[0.9rem] align-top font-semibold text-fg text-[0.85rem] ${border}`}>{row.label}</td>
                    <td class={`px-5 py-[0.9rem] align-top bg-[oklch(14%_0.015_25/_0.3)] text-fg ${border}`}>{row.tanren}</td>
                    <td class={`px-5 py-[0.9rem] align-top text-muted ${border}`}>{row.openai}</td>
                    <td class={`px-5 py-[0.9rem] align-top text-muted ${border}`}>{row.anthropic}</td>
                    <td class={`px-5 py-[0.9rem] align-top text-muted ${border}`}>{row.openclaw}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </section>
  );
}

export default Comparison;
